import type { Contract, JsonSchemaType, User, UserGroup } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Activity,
  CornerUpLeft,
  Edit3,
  Eye,
  FileSignature,
  FileText,
  GitBranch,
  Paperclip,
  Receipt,
  ShieldCheck,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { useContract, useContracts, useUpdateContract, useUserGroups } from '@/core/api';
import type { PayloadApiError } from '@/core/api/client';
import { useGlobalMetadata } from '@/core/api/global-metadata';
import { useObjects } from '@/core/api/objects';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { EntityPermissionsTab, ViewAsDialog } from '@/features/shared/permissions';
import { DetailPageLayout, TabNav } from '@/shared/components/layout';
import { Badge, Button, Form } from '@/shared/components/ui';
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard';
import { useViewAsState } from '@/shared/hooks/use-view-as';
import { getCustomerDetailHref, getObjectDetailHref } from '@/shared/utils';
import {
  createMetadataZodSchema,
  parseMetadata,
  sanitizeMetadataValues,
} from '@/shared/utils/metadata';
import {
  canReadService,
  canUpdateService,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';
import { ContractActivity } from '../components/contract-activity';
import { type BillingContractSource, ContractBillingTab } from '../components/contract-billing-tab';
import { ContractFilesTab } from '../components/contract-files-tab';
import { ContractGeneralTab } from '../components/contract-general-tab';
import { ContractMetadataBuilderDialog } from '../components/contract-metadata-builder-dialog';
import { ContractSubcontractsTab } from '../components/contract-subcontracts-tab';
import { createContractSchema } from '../schema/contract-schema';
import type { ContractBillingPeriodFormData, ContractFormData } from '../types/contract-form';

type RelationshipLike =
  | string
  | { id: string; title?: string; orderNumber?: string }
  | null
  | undefined;

const getRelationshipId = (value: RelationshipLike): string | null => {
  if (!value) return null;
  return typeof value === 'string' ? value : value.id || null;
};

const mapBackendValidationErrors = (
  errors: Array<{ message: string }>,
  form: any,
  t: ReturnType<typeof useIntl>['t']
) => {
  const mapping: Record<
    string,
    | { field: keyof ContractFormData; message: string }
    | Array<{ field: keyof ContractFormData; message: string }>
  > = {
    StartDateAfterEndDate: { field: 'startDate', message: t('validation.StartDateAfterEndDate') },
    EndDateBeforeStartDate: { field: 'endDate', message: t('validation.EndDateBeforeStartDate') },
    BillingPeriodOutsideContractRange: [
      { field: 'startDate', message: t('validation.BillingPeriodOutsideContractRange') },
      { field: 'endDate', message: t('validation.BillingPeriodOutsideContractRange') },
    ],
  };

  const currentValues = form.getValues();
  form.reset(currentValues, { keepValues: true });

  errors.forEach((err) => {
    const target = mapping[err.message];
    if (!target) {
      toast.error(t('common.errorSaving'));
      return;
    }
    if (Array.isArray(target)) {
      target.forEach(({ field, message }) => {
        form.setError(field, { message });
      });
      return;
    }
    form.setError(target.field, { message: target.message });
  });
};

const normalizeBillingInterval = (
  value: unknown,
  fallback?: ContractFormData['billingInterval']
): ContractFormData['billingInterval'] | undefined => {
  if (value === 'monthly' || value === 'quarterly' || value === 'yearly') {
    return value;
  }
  return fallback ?? 'monthly';
};

const normalizeBillingPeriods = (billingPeriods: ContractBillingPeriodFormData[] | undefined) =>
  (billingPeriods || []).map((period) => ({
    id: period.id,
    startDate: period.startDate || '',
    endDate: period.endDate || '',
    amountCents: typeof period.amountCents === 'number' ? period.amountCents : 0,
    status: (period.status || 'scheduled') as ContractBillingPeriodFormData['status'],
    dueDate: period.dueDate || undefined,
    invoiceNumber: period.invoiceNumber || '',
    note: period.note || '',
  }));

const areBillingPeriodsEqual = (
  left: ContractBillingPeriodFormData[] | undefined,
  right: ContractBillingPeriodFormData[] | undefined
) =>
  JSON.stringify(normalizeBillingPeriods(left)) === JSON.stringify(normalizeBillingPeriods(right));

const toFormDefaults = (contract: Contract): ContractFormData => {
  return {
    orderNumber: contract.orderNumber || '',
    weaOssCount: typeof contract.weaOssCount === 'number' ? contract.weaOssCount : undefined,
    title: contract.title || '',
    customer:
      typeof contract.customer === 'string' ? contract.customer : contract.customer?.id || '',
    objects:
      (contract.objects as ({ id: string } | string)[])?.map((obj) =>
        typeof obj === 'string' ? obj : obj.id
      ) || [],
    startDate: contract.startDate || '',
    endDate: contract.endDate || '',
    note: contract.note || '',
    status: (contract.status as ContractFormData['status']) || 'open',
    paymentTermsDays: (contract as any)?.paymentTermsDays ?? 30,
    billingTotalCents: (contract as any)?.billingTotalCents ?? 0,
    billingInterval: normalizeBillingInterval((contract as any)?.billingInterval),
    billingGenerationMode: ((contract as any)?.billingGenerationMode as any) ?? 'generated',
    billingPoNumber: (contract as any)?.billingPoNumber ?? '',
    billingContactName: (contract as any)?.billingContactName ?? '',
    billingContactEmail: (contract as any)?.billingContactEmail ?? '',
    billingPeriods: (((contract as any)?.billingPeriods as any[]) || []).map((p) => ({
      id: p.id,
      startDate: p.startDate || '',
      endDate: p.endDate || '',
      amountCents: typeof p.amountCents === 'number' ? p.amountCents : 0,
      status: p.status || 'scheduled',
      dueDate: p.dueDate || undefined,
      invoiceNumber: p.invoiceNumber || '',
      note: p.note || '',
    })),
    metadata: parseMetadata(contract.metadata as Record<string, unknown>),
    dynamicMetadataSchema: (contract.dynamicMetadataSchema as JsonSchemaType) || undefined,
    dynamicMetadataValues: parseMetadata(contract.dynamicMetadataValues as Record<string, unknown>),
  };
};

export function ContractDetails() {
  const { t } = useIntl();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();

  const { data: currentContract } = useContract(id!, { disabled: !id, depth: 2 });
  const updateContract = useUpdateContract();

  const { data: globalMetadata } = useGlobalMetadata();
  const contractMetadataConfig = globalMetadata?.rootContractMetadata as JsonSchemaType | undefined;
  const { data: userGroupsData } = useUserGroups({ limit: 0, sort: 'key' });
  const userGroups = userGroupsData?.docs ?? [];
  const userGroupsById = useMemo(
    () => new Map(userGroups.map((group) => [group.id, group as UserGroup])),
    [userGroups]
  );

  const resolveGroupFromUser = useCallback(
    (targetUser: User | null | undefined): UserGroup | null => {
      if (!targetUser?.userGroup) return null;
      if (typeof targetUser.userGroup === 'string') {
        return userGroupsById.get(targetUser.userGroup) ?? null;
      }
      return targetUser.userGroup as UserGroup;
    },
    [userGroupsById]
  );

  const currentGroup = resolveGroupFromUser(user);
  const isCurrentGmnAdmin = currentGroup?.key === 'gmn-admin';
  const canUseViewAs = Boolean(isCurrentGmnAdmin && id);

  const { viewAsUser, viewAsAccess, isViewAsActive, setViewAsUserId, clearViewAs } = useViewAsState(
    {
      entityType: 'contracts',
      entityId: id ?? null,
      enabled: canUseViewAs,
    }
  );

  const effectiveUser = viewAsUser ?? user;
  const effectiveGroup = resolveGroupFromUser(effectiveUser);
  const effectiveGroupKey = effectiveGroup?.key ?? null;
  const effectiveDomain = (effectiveGroup?.domain as 'gmn' | 'customer' | undefined) ?? null;
  const isEffectiveGmnAdmin = effectiveGroupKey === 'gmn-admin';
  const isEffectiveCustomerAdmin = effectiveGroupKey === 'customer-admin';
  const canUpdateContracts = effectiveGroup ? canUpdateService(effectiveGroup, 'contracts') : false;
  const canReadObjects = effectiveGroup ? canReadService(effectiveGroup, 'objects') : false;
  const canReadCustomers = effectiveGroup ? canReadService(effectiveGroup, 'customers') : false;
  const effectiveUserCustomerId =
    typeof effectiveUser?.customer === 'object'
      ? effectiveUser.customer?.id
      : (effectiveUser?.customer as string | undefined);
  const viewAsUpdateAllowed = Boolean(viewAsAccess?.update.allowed);
  const canPreviewEditContracts = isViewAsActive ? viewAsUpdateAllowed : canUpdateContracts;
  const canSaveContracts = !isViewAsActive && canPreviewEditContracts;

  const basePath =
    resolveUserGroupDomain(user) === 'customer'
      ? '/c/contract-management/contracts'
      : '/gmn/contract-management/contracts';
  const currentTab = location.pathname.split('/').pop();

  const [viewMode, setViewMode] = useState<'read' | 'edit'>('read');
  const [error, setError] = useState<PayloadApiError | undefined>();
  const [isViewAsDialogOpen, setIsViewAsDialogOpen] = useState(false);

  const [metadataSchemaZod, setMetadataSchemaZod] = useState<z.ZodObject<z.ZodRawShape>>(
    z.object({})
  );
  const [dynamicMetadataSchemaZod, setDynamicMetadataSchemaZod] = useState<
    z.ZodObject<z.ZodRawShape>
  >(z.object({}));

  const [isMetadataDrawerOpen, setIsMetadataDrawerOpen] = useState(false);
  const [drawerSchema, setDrawerSchema] = useState<JsonSchemaType | undefined>(undefined);

  const initialValuesRef = useRef<ContractFormData | null>(null);

  const form = useForm<ContractFormData, any, ContractFormData>({
    resolver: zodResolver(
      createContractSchema(t, metadataSchemaZod, dynamicMetadataSchemaZod)
    ) as any,
    defaultValues: initialValuesRef.current ?? undefined,
  });

  const parentContract = (currentContract as any)?.parentContract as RelationshipLike;
  const parentContractId = getRelationshipId(parentContract);
  const isSubcontract = Boolean(parentContractId);
  const isRootBillingView = currentTab === 'billing' && !isSubcontract;

  const { data: subcontractsData } = useContracts({
    where: {
      parentContract: {
        equals: id,
      },
    },
    sort: 'startDate',
    limit: 100,
    disabled: !id || !isRootBillingView,
  });
  const subcontractContracts = useMemo(
    () => subcontractsData?.docs ?? [],
    [subcontractsData?.docs]
  );
  const [subcontractBillingDrafts, setSubcontractBillingDrafts] = useState<
    Record<string, ContractBillingPeriodFormData[]>
  >({});

  useEffect(() => {
    if (!contractMetadataConfig) return;
    setMetadataSchemaZod(createMetadataZodSchema(contractMetadataConfig, effectiveGroupKey));
  }, [contractMetadataConfig, effectiveGroupKey]);

  useEffect(() => {
    const schema = currentContract?.dynamicMetadataSchema as JsonSchemaType | undefined;
    if (schema) {
      setDynamicMetadataSchemaZod(createMetadataZodSchema(schema, effectiveGroupKey));
      return;
    }
    setDynamicMetadataSchemaZod(z.object({}));
  }, [currentContract?.dynamicMetadataSchema, effectiveGroupKey]);

  const contractSignature = currentContract
    ? `${currentContract.id}:${currentContract.updatedAt ?? ''}:${(currentContract as any).billingInterval ?? ''}:${(currentContract as any).billingTotalCents ?? ''}`
    : null;
  const subcontractDraftSignature = subcontractContracts
    .map(
      (contract) =>
        `${contract.id}:${contract.updatedAt ?? ''}:${JSON.stringify(
          normalizeBillingPeriods((((contract as any).billingPeriods as any[]) || []) as any)
        )}`
    )
    .join('|');

  useEffect(() => {
    if (!currentContract) return;
    const defaults = toFormDefaults(currentContract);
    initialValuesRef.current = defaults;
    form.reset(defaults);
    setSubcontractBillingDrafts(
      Object.fromEntries(
        subcontractContracts.map((contract) => [
          contract.id,
          normalizeBillingPeriods((((contract as any).billingPeriods as any[]) || []) as any),
        ])
      )
    );
    setViewMode('read');
  }, [contractSignature, subcontractDraftSignature]);

  useEffect(() => {
    if (!globalMetadata) return;

    const staticSchema = globalMetadata.rootContractMetadata as JsonSchemaType | undefined;
    const currentMeta = (form.getValues().metadata || {}) as Record<string, unknown>;
    const sanitizedMeta = sanitizeMetadataValues(currentMeta, staticSchema);
    if (JSON.stringify(currentMeta) !== JSON.stringify(sanitizedMeta)) {
      form.setValue('metadata', sanitizedMeta, { shouldValidate: false });
    }

    const currentDynValues = (form.getValues().dynamicMetadataValues || {}) as Record<
      string,
      unknown
    >;
    const sanitizedDyn = sanitizeMetadataValues(
      currentDynValues,
      form.getValues().dynamicMetadataSchema as JsonSchemaType
    );
    if (JSON.stringify(currentDynValues) !== JSON.stringify(sanitizedDyn)) {
      form.setValue('dynamicMetadataValues', sanitizedDyn, { shouldValidate: false });
    }
  }, [globalMetadata, form, metadataSchemaZod, dynamicMetadataSchemaZod]);

  useEffect(() => {
    if (!error?.response?.errors) return;
    mapBackendValidationErrors(error.response.errors as Array<{ message: string }>, form, t);
  }, [error, form, t]);

  const contractAggregatedObjectIds =
    (currentContract?.aggregatedObjects as string[] | Array<{ id: string }>)?.map((obj) =>
      typeof obj === 'string' ? obj : obj.id
    ) || [];

  const selectedObjectIds = form.watch('objects') || [];
  const aggregatedObjectIds = viewMode === 'edit' ? selectedObjectIds : contractAggregatedObjectIds;

  const { data: objectsData, isLoading: isLoadingObjects } = useObjects({
    where: { id: { in: aggregatedObjectIds } },
    limit: aggregatedObjectIds.length,
    disabled: !canReadObjects || aggregatedObjectIds.length === 0,
  });

  const customerName =
    currentContract?.customer && typeof currentContract.customer === 'object'
      ? currentContract.customer.name
      : undefined;
  const contractCustomerId =
    currentContract?.customer && typeof currentContract.customer === 'object'
      ? currentContract.customer.id
      : typeof currentContract?.customer === 'string'
        ? currentContract.customer
        : undefined;
  const contractCustomerType =
    currentContract?.customer && typeof currentContract.customer === 'object'
      ? currentContract.customer.customerType
      : null;
  const contractCustomerHref = getCustomerDetailHref({
    customerId: contractCustomerId,
    customerType: contractCustomerType,
    domain: effectiveDomain,
    canReadCustomers,
    effectiveUserCustomerId,
  });
  const aggregatedObjectsForView = (objectsData?.docs || []).map((object) => ({
    id: object.id,
    name: object.name,
    href: getObjectDetailHref({
      objectId: object.id,
      domain: effectiveDomain,
      canReadObjects,
    }),
  }));

  const viewAsUserLabel = useMemo(() => {
    if (!viewAsUser) return '';
    const fullName = `${viewAsUser.firstName ?? ''} ${viewAsUser.lastName ?? ''}`.trim();
    return fullName !== '' ? fullName : viewAsUser.email || viewAsUser.id;
  }, [viewAsUser]);

  const openMetadataDrawer = () => {
    setDrawerSchema(form.getValues().dynamicMetadataSchema);
    setIsMetadataDrawerOpen(true);
  };

  const handleMetadataBuilderSave = () => {
    form.setValue('dynamicMetadataSchema', drawerSchema);
    if (drawerSchema) {
      setDynamicMetadataSchemaZod(createMetadataZodSchema(drawerSchema, effectiveGroupKey));
    } else {
      setDynamicMetadataSchemaZod(z.object({}));
    }
    setIsMetadataDrawerOpen(false);
  };

  const discardChanges = useCallback(() => {
    if (initialValuesRef.current) {
      form.reset(initialValuesRef.current);
      const schema = initialValuesRef.current.dynamicMetadataSchema as JsonSchemaType | undefined;
      setDynamicMetadataSchemaZod(
        schema ? createMetadataZodSchema(schema, effectiveGroupKey) : z.object({})
      );
    }
    setSubcontractBillingDrafts(
      Object.fromEntries(
        subcontractContracts.map((contract) => [
          contract.id,
          normalizeBillingPeriods((((contract as any).billingPeriods as any[]) || []) as any),
        ])
      )
    );
    setViewMode('read');
    setError(undefined);
    setIsMetadataDrawerOpen(false);
  }, [effectiveGroupKey, form, subcontractContracts]);

  const hasDirtySubcontractBillingDrafts = useMemo(
    () =>
      subcontractContracts.some((contract) => {
        const initialBillingPeriods = normalizeBillingPeriods(
          ((((contract as any).billingPeriods as any[]) || []) as any) ?? []
        );
        const currentDraft = subcontractBillingDrafts[contract.id] ?? initialBillingPeriods;
        return !areBillingPeriodsEqual(initialBillingPeriods, currentDraft);
      }),
    [subcontractBillingDrafts, subcontractContracts]
  );

  const { dialog: unsavedDialog } = useUnsavedChangesGuard({
    when: (form.formState.isDirty || hasDirtySubcontractBillingDrafts) && !isViewAsActive,
    onDiscard: discardChanges,
  });

  const previousViewAsRef = useRef(false);
  const previousViewAsUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id) return;

    if (isViewAsActive) {
      const currentViewAsUserId = viewAsUser?.id ?? null;
      const userChanged = previousViewAsUserRef.current !== currentViewAsUserId;
      if (!previousViewAsRef.current || userChanged) {
        discardChanges();
      }

      setViewMode(viewAsUpdateAllowed ? 'edit' : 'read');
      previousViewAsUserRef.current = currentViewAsUserId;
    } else if (previousViewAsRef.current) {
      setViewMode('read');
      previousViewAsUserRef.current = null;
    }

    previousViewAsRef.current = isViewAsActive;
  }, [discardChanges, id, isViewAsActive, viewAsUpdateAllowed, viewAsUser?.id]);

  useEffect(() => {
    if (!isViewAsActive || viewAsUpdateAllowed) return;
    if (viewMode !== 'read' || form.formState.isDirty) {
      discardChanges();
    }
  }, [discardChanges, form.formState.isDirty, isViewAsActive, viewAsUpdateAllowed, viewMode]);

  useEffect(() => {
    if (
      (currentTab === 'activity' || currentTab === 'files' || currentTab === 'subcontracts') &&
      viewMode === 'edit'
    ) {
      discardChanges();
    }
  }, [currentTab, discardChanges, viewMode]);

  const submitForm = () => {
    if (isRootBillingView) {
      void form.handleSubmit(handleRootBillingSubmit)();
      return;
    }
    const formElement = document.getElementById('contract-form') as HTMLFormElement | null;
    formElement?.requestSubmit();
  };

  const saveContractUpdate = async ({
    targetId,
    data,
  }: {
    targetId: string;
    data: Partial<ContractFormData>;
  }) => {
    return updateContract.mutateAsync({
      id: targetId,
      data,
    });
  };

  const handleRootBillingSubmit = async (data: ContractFormData) => {
    if (!id || !currentContract || !isRootBillingView) return;
    if (!canSaveContracts) return;

    try {
      setError(undefined);
      const normalizedData: ContractFormData = {
        ...data,
        billingPeriods: normalizeBillingPeriods(data.billingPeriods),
        billingInterval: normalizeBillingInterval(
          data.billingInterval,
          normalizeBillingInterval((currentContract as any)?.billingInterval)
        ),
      };

      const dirtySubcontracts = subcontractContracts
        .map((contract) => {
          const originalBillingPeriods = normalizeBillingPeriods(
            ((((contract as any).billingPeriods as any[]) || []) as any) ?? []
          );
          const nextBillingPeriods =
            subcontractBillingDrafts[contract.id] ?? originalBillingPeriods;
          return {
            contract,
            originalBillingPeriods,
            nextBillingPeriods,
            isDirty: !areBillingPeriodsEqual(originalBillingPeriods, nextBillingPeriods),
          };
        })
        .filter(({ isDirty }) => isDirty);

      let nextRootDefaults: ContractFormData | null = null;
      if (form.formState.isDirty) {
        const rootResponse = await saveContractUpdate({
          targetId: currentContract.id,
          data: normalizedData,
        });
        nextRootDefaults = toFormDefaults(rootResponse);
      }

      for (const { contract, nextBillingPeriods } of dirtySubcontracts) {
        try {
          await saveContractUpdate({
            targetId: contract.id,
            data: { billingPeriods: normalizeBillingPeriods(nextBillingPeriods) },
          });
        } catch (err) {
          toast.error(
            t('contracts.billingSaveFailedFor', {
              name: contract.title || contract.orderNumber || contract.id,
            })
          );
          throw err;
        }
      }

      if (nextRootDefaults) {
        initialValuesRef.current = nextRootDefaults;
        form.reset(nextRootDefaults);
      } else if (initialValuesRef.current) {
        form.reset(initialValuesRef.current, { keepValues: true });
      }

      setSubcontractBillingDrafts(
        Object.fromEntries(
          subcontractContracts.map((contract) => [
            contract.id,
            normalizeBillingPeriods(
              contract.id === currentContract.id
                ? normalizedData.billingPeriods
                : dirtySubcontracts.find((entry) => entry.contract.id === contract.id)
                    ?.nextBillingPeriods ||
                    ((((contract as any).billingPeriods as any[]) || []) as any)
            ),
          ])
        )
      );
      toast.success(t('common.savedSuccessfully'));
    } catch (err) {
      setError(err as PayloadApiError);
      toast.error(t('common.errorSaving'));
    }
  };

  const handleSubmit = async (data: ContractFormData) => {
    if (!id || !currentContract) return;
    if (!canSaveContracts) return;
    try {
      const normalizedData: ContractFormData = {
        ...data,
        billingInterval: normalizeBillingInterval(
          data.billingInterval,
          normalizeBillingInterval((currentContract as any)?.billingInterval)
        ),
      };
      setError(undefined);
      const response = await updateContract.mutateAsync({
        id: currentContract.id,
        data: normalizedData,
      });
      const nextDefaults = toFormDefaults(response);
      initialValuesRef.current = nextDefaults;
      form.reset(nextDefaults);
      setViewMode('read');
      toast.success(t('common.savedSuccessfully'));
    } catch (err) {
      setError(err as PayloadApiError);
      toast.error(t('common.errorSaving'));
    }
  };

  if (!currentContract) {
    return null;
  }

  const parentLabel = !parentContractId
    ? null
    : typeof parentContract === 'string'
      ? t('contracts.viewParentContract')
      : parentContract?.title || parentContract?.orderNumber || t('contracts.viewParentContract');

  const subcontractBillingSources = subcontractContracts.map(
    (contract): BillingContractSource => ({
      contractId: contract.id,
      contractLabel: contract.title || contract.orderNumber || contract.id,
      isRootContract: false,
      startDate: contract.startDate || '',
      endDate: contract.endDate || '',
      paymentTermsDays: ((contract as any).paymentTermsDays as number | undefined) ?? 30,
      billingPeriods:
        subcontractBillingDrafts[contract.id] ??
        normalizeBillingPeriods((((contract as any).billingPeriods as any[]) || []) as any),
    })
  );

  const tabs = [
    { value: 'general', label: t('common.general'), icon: FileText },
    { value: 'billing', label: t('contracts.billingTab'), icon: Receipt },
    { value: 'files', label: t('contracts.files.tab'), icon: Paperclip },
    ...(!isSubcontract
      ? [{ value: 'subcontracts', label: t('contracts.subcontracts'), icon: GitBranch }]
      : []),
    ...((isEffectiveGmnAdmin || isEffectiveCustomerAdmin) && !isViewAsActive
      ? [{ value: 'permissions', label: t('permissions.tab'), icon: ShieldCheck }]
      : []),
    { value: 'activity', label: t('common.activity'), icon: Activity },
  ];

  const viewAsControls = canUseViewAs ? (
    <div className="flex flex-wrap items-center gap-2">
      {isViewAsActive && viewAsUserLabel ? (
        <Badge variant="outline-solid" data-testid="view-as-indicator">
          <Eye size={14} className="mr-1" />
          {t('permissions.viewAs.activeLabel', { name: viewAsUserLabel })}
        </Badge>
      ) : null}
      {!isViewAsActive ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsViewAsDialogOpen(true)}
          data-testid="view-as-open"
        >
          <Eye size={16} className="mr-2" />
          {t('permissions.viewAs.label')}
        </Button>
      ) : null}
      {isViewAsActive ? (
        <Button type="button" variant="outline" onClick={clearViewAs} data-testid="view-as-exit">
          <X size={16} className="mr-2" />
          {t('permissions.viewAs.exit')}
        </Button>
      ) : null}
    </div>
  ) : null;

  const showActions =
    currentTab !== 'activity' &&
    currentTab !== 'files' &&
    currentTab !== 'subcontracts' &&
    currentTab !== 'permissions';
  const isBillingTab = currentTab === 'billing';
  const isEditing = viewMode === 'edit' || (isBillingTab && canPreviewEditContracts);

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        backHref: basePath,
        backLabel: t('contracts.backToOverview'),
        title:
          currentContract.title || currentContract.orderNumber || t('contracts.contractDetails'),
        icon: FileSignature,
        metaItems: [
          ...(parentContractId && parentLabel
            ? [
                {
                  icon: CornerUpLeft,
                  label: t('contracts.subcontractOf', { name: parentLabel }),
                  href: `${basePath}/${parentContractId}/general`,
                },
              ]
            : []),
          ...(currentContract.orderNumber
            ? [{ icon: Tag, label: currentContract.orderNumber }]
            : []),
          ...(customerName
            ? [{ icon: Users, label: customerName, href: contractCustomerHref ?? undefined }]
            : []),
        ],
        actions:
          showActions && (!isEditing || isViewAsActive) ? (
            <div className="flex flex-wrap items-center gap-3">
              {viewAsControls}
              {!isEditing && canSaveContracts ? (
                <Button
                  type="button"
                  onClick={() => setViewMode('edit')}
                  data-testid="contract-enter-edit"
                >
                  <Edit3 size={16} className="mr-2" />
                  {t('common.edit')}
                </Button>
              ) : null}
            </div>
          ) : undefined,
        secondaryAction:
          showActions && isEditing && !isViewAsActive
            ? {
                label: t('common.discard'),
                onClick: discardChanges,
              }
            : undefined,
        primaryAction:
          showActions && isEditing && !isViewAsActive
            ? {
                label: updateContract.isPending ? t('common.saving') : t('common.save'),
                onClick: submitForm,
                disabled: !canSaveContracts || updateContract.isPending,
                loading: updateContract.isPending,
              }
            : undefined,
      }}
    >
      <TabNav tabs={tabs} basePath={`${basePath}/${id}`} variant="underline" />

      <Form {...form}>
        <form id="contract-form" onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-6">
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />

          {currentTab === 'general' && (
            <ContractGeneralTab
              mode={viewMode}
              form={form}
              canEditContracts={canPreviewEditContracts}
              canReadObjects={canReadObjects}
              isGmnAdmin={isEffectiveGmnAdmin}
              groupKey={effectiveGroupKey}
              disableCustomerSelection={isSubcontract}
              customerName={customerName}
              customerHref={contractCustomerHref}
              aggregatedObjectIds={aggregatedObjectIds}
              aggregatedObjects={aggregatedObjectsForView}
              isLoadingObjects={isLoadingObjects}
              metadataConfig={contractMetadataConfig}
              metadataSchemaZod={metadataSchemaZod}
              dynamicSchema={form.watch('dynamicMetadataSchema') as JsonSchemaType | undefined}
              dynamicSchemaZod={dynamicMetadataSchemaZod}
              onEditDynamicSchema={
                viewMode === 'edit' && isEffectiveGmnAdmin && !isViewAsActive
                  ? openMetadataDrawer
                  : undefined
              }
            />
          )}

          {currentTab === 'billing' && (
            <ContractBillingTab
              contractId={currentContract.id}
              form={form}
              fallbackBillingInterval={normalizeBillingInterval(
                (currentContract as any)?.billingInterval
              )}
              readOnly={!canPreviewEditContracts}
              subcontractSources={isRootBillingView ? subcontractBillingSources : []}
              onSubcontractBillingPeriodsChange={
                isRootBillingView
                  ? (targetContractId, billingPeriods) => {
                      setSubcontractBillingDrafts((current) => ({
                        ...current,
                        [targetContractId]: normalizeBillingPeriods(billingPeriods),
                      }));
                    }
                  : undefined
              }
            />
          )}

          {currentTab === 'files' && (
            <ContractFilesTab
              contractId={currentContract.id}
              canManageFiles={canPreviewEditContracts && !isViewAsActive}
            />
          )}

          {currentTab === 'subcontracts' && !isSubcontract && (
            <ContractSubcontractsTab
              contractId={id!}
              basePath={basePath}
              isGmnAdmin={isEffectiveGmnAdmin}
            />
          )}

          {currentTab === 'permissions' && (
            <EntityPermissionsTab
              entityId={id!}
              entityType="contracts"
              customerContexts={
                contractCustomerId
                  ? [{ customerId: contractCustomerId, source: 'assigned-customer' }]
                  : []
              }
            />
          )}

          {currentTab === 'activity' && (
            <ContractActivity contractId={id} groupKey={effectiveGroupKey} />
          )}
        </form>
      </Form>

      <ContractMetadataBuilderDialog
        open={isMetadataDrawerOpen}
        value={drawerSchema}
        onChange={setDrawerSchema}
        onCancel={() => setIsMetadataDrawerOpen(false)}
        onSave={handleMetadataBuilderSave}
        userGroups={userGroups}
      />

      {canUseViewAs && id ? (
        <ViewAsDialog
          open={isViewAsDialogOpen}
          onOpenChange={setIsViewAsDialogOpen}
          entityType="contracts"
          entityId={id}
          customerContexts={
            contractCustomerId
              ? [{ customerId: contractCustomerId, source: 'assigned-customer' }]
              : []
          }
          selectedUserId={viewAsUser?.id ?? null}
          onSelectUser={(selectedUser) => {
            setViewAsUserId(selectedUser.id);
            setIsViewAsDialogOpen(false);
          }}
        />
      ) : null}

      {unsavedDialog}
    </DetailPageLayout>
  );
}
